import { useState } from 'react';
import { IconArchive } from '@tabler/icons-react';
import toast from 'react-hot-toast';
import { IndeterminateCheckbox, useDialog } from 'ui-kit';
import ConfirmDialog from 'ui-kit/common/ConfirmDialog';

import { useMutation } from '@apollo/client';

import { FileType } from '__generated__/globalTypes';
import { DeleteS3File, DeleteS3FileVariables } from 'mutation/__generated__/deleteS3File';
import DELETE_S3_FILE_MUTATION from 'mutation/deleteS3File';

import { UPDATE_FILE_TEMPLATE } from 'query/documentLibrary';

import { getFileIcon } from 'utils/fileIconUtils';

import TemplateToastContent from './TemplateToastContent';

import { Document } from './__generated__/DocumentDetails.fragments';
type InProgressFile = {
  id?: string;
  name: string;
  lastModified: number;
  progress?: {
    percentage: number;
  };
};

interface PassedProps {
  documents: Document[];
  companyId: string;
  fileType: FileType;
}

function DocumentDetails({ documents, companyId, fileType }: PassedProps) {
  const [updateFileTemplate] = useMutation(UPDATE_FILE_TEMPLATE, {
    refetchQueries: ['GetDocumentLibrary'],
  });

  const [archiveDocument, { loading: archiving }] = useMutation<
    DeleteS3File,
    DeleteS3FileVariables
  >(DELETE_S3_FILE_MUTATION, { refetchQueries: ['GetDocumentLibrary'] });

  const archiveConfirmDialog = useDialog();
  const [pendingArchive, setPendingArchive] = useState<Document | null>(null);

  const toggleTemplate = async (document: Document) => {
    const isCreatingTemplate = !document.is_template;

    await updateFileTemplate({
      variables: {
        id: document.id,
        isTemplate: !document.is_template,
      },
    });

    const toastId = toast(
      <TemplateToastContent
        documentName={document.name}
        isCreatingTemplate={isCreatingTemplate}
        onClose={() => toast.dismiss(toastId)}
      />,
    );
  };

  const requestArchive = (document: Document) => {
    setPendingArchive(document);
    archiveConfirmDialog.openDialog();
  };

  const confirmArchive = async () => {
    if (!pendingArchive) return;
    const target = pendingArchive;
    setPendingArchive(null);
    try {
      await archiveDocument({
        variables: {
          parentId: companyId,
          fileType,
          fileName: target.name,
        },
      });
      toast.success(`Archived "${target.name}"`);
    } catch (error) {
      toast.error(`Failed to archive "${target.name}"`);
    }
  };

  // TODO keep a list of documents that are uploading and filter out records coming from docs prop

  return (
    <>
      <div className="grid grid-cols-2 gap-x-4">
        {documents &&
          documents.map((document: Document) => {
            return (
              <div key={document.id}>
                <div className="flex items-center justify-between border-t border-slate-600 p-2 text-slate-400 hover:bg-accent-default/15">
                  <span>{document.name}</span>
                  <div className="flex items-center">
                    {document.documentUrl != null && (
                      <a
                        className="flex px-2 text-slate-400 no-underline"
                        target="blank"
                        href={document.documentUrl}
                        download
                      >
                        {getFileIcon(document.name)}
                        <span className="pl-2 text-accent-default">View</span>
                      </a>
                    )}
                    <IndeterminateCheckbox
                      checked={document.is_template}
                      onChange={() => toggleTemplate(document)}
                    />
                    <button
                      className="flex cursor-pointer items-center border-0 bg-transparent px-2 text-slate-400 disabled:cursor-not-allowed disabled:opacity-50"
                      type="button"
                      aria-label={`Archive ${document.name}`}
                      onClick={() => requestArchive(document)}
                      disabled={archiving}
                      data-testid={`archive-document-${document.id}`}
                    >
                      <IconArchive size={20} />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
      </div>
      <ConfirmDialog
        dialog={archiveConfirmDialog}
        message={
          pendingArchive?.is_template
            ? `"${pendingArchive?.name}" is currently flagged as a template. Archive it? You can restore it later by enabling "Show archived templates".`
            : `Archive "${pendingArchive?.name}"?`
        }
        confirmButtonText="Archive"
        onConfirm={confirmArchive}
      />
    </>
  );
}

export default DocumentDetails;
export type { InProgressFile };
